import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Chip,
  Avatar,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  CircularProgress,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  Badge,
  Divider,
  useTheme,
  Checkbox
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import { DataGrid } from '@mui/x-data-grid';
import { useFacilityChangeListener } from '../../hooks/useFacilityChangeListener';
import { useNavigate } from 'react-router-dom';
import PatientHoverModal from './PatientHoverModal';

// Styled Components
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(1.5),
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
}));

const SearchCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
}));

const PatientCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  borderRadius: '12px',
  '& .MuiDataGrid-cell': {
    fontSize: '0.875rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 400,
    color: 'rgba(0, 0, 0, 0.87)',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
    borderRadius: '12px 12px 0 0',
    fontSize: '0.875rem',
  },
  '& .MuiDataGrid-row': {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 400,
    color: 'rgba(0, 0, 0, 0.87)',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PatientListBase = ({
  patients = [],
  loading = false,
  error = null,
  searchTerm = '',
  onSearchChange,
  onPatientClick,
  onPatientAction,
  onCreatePatient,
  showRecentSearches = true,
  recentSearches = [],
  onRecentSearchClick,
  onClearRecentSearches,
  facilities = {},
  ongoingEncounters = {},
  lastEncounters = {},
  role = 'user',
  title = 'Patients',
  subtitle = 'Manage patient records and information',
}) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState('table'); // 'cards' or 'table'
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    bloodType: 'all',
    hasOngoingEncounter: 'all',
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(12);
  const [hoveredPatient, setHoveredPatient] = useState({ data: null, anchorEl: null, position: null });
  const navigate = useNavigate();

  // Listen for facility changes and refresh data
  const handleFacilityChange = (newFacilityId, previousFacilityId) => {
    console.log('PatientListBase: Facility changed, refreshing data');
    // This component receives data as props, so the parent component will handle the refresh
  };

  useFacilityChangeListener(handleFacilityChange);

  // Calculate patient age
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'Unknown';
    
    try {
      let date;
      // If it's already a formatted string, try to parse it
      if (typeof dateOfBirth === 'string' && dateOfBirth.includes('/')) {
        // Try to parse common date formats
        const parts = dateOfBirth.split('/');
        if (parts.length === 3) {
          // Assume MM/DD/YYYY or DD/MM/YYYY format
          const month = parseInt(parts[0]) - 1; // Month is 0-indexed
          const day = parseInt(parts[1]);
          const year = parseInt(parts[2]);
          date = new Date(year, month, day);
        } else {
          date = new Date(dateOfBirth);
        }
      } else {
        date = new Date(dateOfBirth);
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Unknown';
      }
      
      const today = new Date();
      let age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
        age--;
      }
      return age;
    } catch {
      return 'Unknown';
    }
  };

  const formatDateOfBirth = (dateOfBirth) => {
    if (!dateOfBirth) return 'Not specified';
    // Firestore Timestamp object
    if (typeof dateOfBirth === 'object' && dateOfBirth.seconds) {
      const date = new Date(dateOfBirth.seconds * 1000);
      if (isNaN(date.getTime())) return 'Not specified';
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    // ISO string or number
    if (typeof dateOfBirth === 'string' || typeof dateOfBirth === 'number') {
      // Check for invalid strings
      const lower = String(dateOfBirth).toLowerCase();
      if (lower.includes('not available') || lower.includes('not specified') || lower.includes('n/a')) {
        return 'Not specified';
      }
      const date = new Date(dateOfBirth);
      if (isNaN(date.getTime())) return 'Not specified';
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    return 'Not specified';
  };

  // Get patient display name (prioritizes firstName + lastName, falls back to name)
  const getPatientDisplayName = (patient) => {
    if (patient.firstName && patient.lastName) {
      return `${patient.firstName} ${patient.lastName}`;
    } else if (patient.name) {
      return patient.name;
    } else if (patient.firstName) {
      return patient.firstName;
    } else if (patient.lastName) {
      return patient.lastName;
    }
    return 'Unknown Patient';
  };

  // Get patient initials for avatar
  const getInitials = (patient) => {
    const displayName = getPatientDisplayName(patient);
    if (!displayName || displayName === 'Unknown Patient') return 'P';
    return displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Filter patients based on search and filters
  const filteredPatients = patients.filter(patient => {
    const displayName = getPatientDisplayName(patient);
    const matchesSearch = !searchTerm || 
      displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.mrn?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesBloodType = selectedFilters.bloodType === 'all' || 
      patient.bloodType === selectedFilters.bloodType;

    const matchesEncounter = selectedFilters.hasOngoingEncounter === 'all' ||
      (selectedFilters.hasOngoingEncounter === 'yes' && ongoingEncounters[patient.id]) ||
      (selectedFilters.hasOngoingEncounter === 'no' && !ongoingEncounters[patient.id]);

    return matchesSearch && matchesBloodType && matchesEncounter;
  });

  // Paginate filtered patients
  const totalPages = Math.ceil(filteredPatients.length / pageSize);
  const paginatedPatients = filteredPatients.slice((page - 1) * pageSize, page * pageSize);

  const handleFilterClick = (event) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setSelectedFilters({
      bloodType: 'all',
      hasOngoingEncounter: 'all',
    });
    setPage(1);
  };

  const getActiveFiltersCount = () => {
    return Object.values(selectedFilters).filter(value => value !== 'all').length;
  };

  const handleMouseEnter = (event, patient) => {
    event.preventDefault();
    event.stopPropagation();
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const rect = event.currentTarget.getBoundingClientRect();
    const modalWidth = 300;
    const modalHeight = 400;
    
    let left = rect.right + 10;
    if (left + modalWidth > viewportWidth) {
      left = rect.left - modalWidth - 10;
    }

    let top = rect.top;
    if (top + modalHeight > viewportHeight) {
      top = viewportHeight - modalHeight - 10;
    }
    if (top < 0) {
      top = 10;
    }
    
    const facilityId = patient.facilityId;
    const facilityName = facilities[facilityId] || 'Not Assigned';
    
    setHoveredPatient({
      data: {
        name: getPatientDisplayName(patient),
        mrn: patient.mrn,
        profileImage: patient.profileImage,
        facilityName: facilityName,
        bloodType: patient.bloodType,
        bmi: patient.bmi || 0,
        medicalActivity: [
          { facilityName: facilityName, date: new Date().toISOString() }
        ]
      },
      anchorEl: event.currentTarget,
      position: { left, top }
    });
  };

  const handleMouseLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setHoveredPatient({ data: null, anchorEl: null, position: null });
  };

  // Render patient card
  const renderPatientCard = (patient) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={patient.id}>
      <PatientCard onClick={() => onPatientClick && onPatientClick(patient)}>
        <CardContent sx={{ p: 2 }}>
          {/* Header with Avatar and Actions */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: theme.palette.primary.main,
                fontSize: '1.1rem',
                fontWeight: 600,
                mr: 2
              }}
            >
              {getInitials(patient)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5, lineHeight: 1.2 }}>
                {getPatientDisplayName(patient)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {patient.mrn ? (patient.mrn.startsWith('MRN') ? patient.mrn : `MRN: ${patient.mrn}`) : 'No MRN'}
              </Typography>
              {/* Last Encounter Information */}
              {(() => {
                const patientId = patient.id;
                const lastEncounter = lastEncounters[patientId];
                const ongoingEncounter = ongoingEncounters[patientId];
                
                if (ongoingEncounter) {
                  // Show active encounter as "Last Encounter"
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label="Active"
                        size="small"
                        color="success"
                        sx={{ fontSize: '0.7rem', height: 20, alignSelf: 'flex-start' }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {ongoingEncounter.encounterType || 'Encounter'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {ongoingEncounter.createdAt ? new Date(ongoingEncounter.createdAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                  );
                } else if (lastEncounter) {
                  // Show last encounter details
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                        {lastEncounter.encounterType || 'Encounter'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {lastEncounter.createdAt ? new Date(lastEncounter.createdAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                      <Chip
                        label={lastEncounter.status || 'Unknown'}
                        size="small"
                        color={lastEncounter.status === 'completed' ? 'default' : 'primary'}
                        sx={{ fontSize: '0.6rem', height: 18, alignSelf: 'flex-start' }}
                      />
                    </Box>
                  );
                } else {
                  // No encounters found
                  return (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      No encounters
                    </Typography>
                  );
                }
              })()}
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onPatientAction && onPatientAction(patient, 'menu');
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Patient Details */}
          <Box sx={{ mb: 2 }}>
            {patient.dateOfBirth && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <CalendarTodayIcon sx={{ fontSize: '0.875rem', color: 'text.secondary', mr: 1 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="caption" color="text.secondary">
                    DOB: {formatDateOfBirth(patient.dateOfBirth)}
                  </Typography>
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                    Age: {calculateAge(patient.dateOfBirth)} years
                  </Typography>
                </Box>
              </Box>
            )}
            {patient.bloodType && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <BloodtypeIcon sx={{ fontSize: '0.875rem', color: 'text.secondary', mr: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {patient.bloodType}
                </Typography>
              </Box>
            )}
            {patient.contact && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <PhoneIcon sx={{ fontSize: '0.875rem', color: 'text.secondary', mr: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {patient.contact}
                </Typography>
              </Box>
            )}
            {facilities[patient.facilityId] && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <LocalHospitalIcon sx={{ fontSize: '0.875rem', color: 'text.secondary', mr: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {facilities[patient.facilityId]}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {ongoingEncounters[patient.id] && (
              <Tooltip title="View Ongoing Encounter">
                <IconButton
                  size="small"
                  color="warning"
                  sx={{ bgcolor: 'warning.light', color: 'white', '&:hover': { bgcolor: 'warning.main' } }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPatientAction && onPatientAction(patient, 'viewEncounter');
                  }}
                >
                  <AssignmentIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Start New Encounter">
              <IconButton
                size="small"
                color="primary"
                sx={{ bgcolor: 'primary.light', color: 'white', '&:hover': { bgcolor: 'primary.main' } }}
                onClick={(e) => {
                  e.stopPropagation();
                  onPatientAction && onPatientAction(patient, 'newEncounter');
                }}
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                color="secondary"
                sx={{ bgcolor: 'secondary.light', color: 'white', '&:hover': { bgcolor: 'secondary.main' } }}
                onClick={(e) => {
                  e.stopPropagation();
                  onPatientClick && onPatientClick(patient);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </PatientCard>
    </Grid>
  );

  // DataGrid columns for table view
  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.5,
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        return (
          <div
            onMouseEnter={(e) => handleMouseEnter(e, params.row)}
            onMouseLeave={handleMouseLeave}
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '8px',
              justifyContent: 'flex-start',
            }}
          >
            {params.value}
          </div>
        );
      },
    },
    {
      field: 'mrn',
      headerName: 'MRN',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'dateOfBirth',
      headerName: 'Date of Birth',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        if (!params || !params.row) {
          return <span>Not specified</span>;
        }
        return <span>{formatDateOfBirth(params.row.dateOfBirth)}</span>;
      },
    },
    {
      field: 'age',
      headerName: 'Age',
      flex: 0.5,
      minWidth: 80,
      renderCell: (params) => {
        if (!params || !params.row) {
          return <Typography variant="body2" color="text.secondary">Unknown</Typography>;
        }
        const age = calculateAge(params.row.dateOfBirth);
        if (age === 'Unknown') {
          return <Typography variant="body2" color="text.secondary">Unknown</Typography>;
        }
        return (
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {age} yrs
          </Typography>
        );
      },
    },
    {
      field: 'contact',
      headerName: 'Contact',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'bloodType',
      headerName: 'Blood Type',
      flex: 0.7,
      minWidth: 100,
    },
    {
      field: 'lastEncounter',
      headerName: 'Last Encounter',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        const patientId = params.row.id;
        const lastEncounter = lastEncounters[patientId];
        const ongoingEncounter = ongoingEncounters[patientId];
        const navigateToEncounter = (encounterId) => {
          navigate(`/encounters/${encounterId}`);
        };
        if (ongoingEncounter) {
          // Show only the date as a clickable link for active encounter
          return (
            ongoingEncounter.createdAt ? (
              <Button
                variant="text"
                size="small"
                sx={{ minWidth: 0, p: 0, fontSize: '0.92rem', textTransform: 'none' }}
                onClick={() => navigateToEncounter(ongoingEncounter.id)}
              >
                {new Date(ongoingEncounter.createdAt).toLocaleDateString()}
              </Button>
            ) : 'N/A'
          );
        } else if (lastEncounter) {
          // Show only the date as a clickable link for last encounter
          return (
            lastEncounter.createdAt ? (
              <Button
                variant="text"
                size="small"
                sx={{ minWidth: 0, p: 0, fontSize: '0.92rem', textTransform: 'none' }}
                onClick={() => navigateToEncounter(lastEncounter.id)}
              >
                {new Date(lastEncounter.createdAt).toLocaleDateString()}
              </Button>
            ) : 'N/A'
          );
        } else {
          // No encounters found
          return (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              No encounters
            </Typography>
          );
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {ongoingEncounters[params.row.id] && (
              <Tooltip title="View Ongoing Encounter">
                <IconButton size="small" color="warning">
                  <AssignmentIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Start Encounter">
              <IconButton size="small" color="primary">
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Details">
              <IconButton size="small" color="secondary">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },

  ];

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Header */}
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
              {title}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
              onClick={onCreatePatient}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                px: 2,
                py: 0.75
              }}
            >
              New Patient
            </Button>
          </Box>
        </CardContent>
      </HeaderCard>

      {/* Search and Filters */}
      <SearchCard>
        <CardContent sx={{ pb: '16px !important' }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search Bar */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search patients by name, MRN, or email..."
                value={searchTerm}
                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                }}
              />
            </Grid>

            {/* Recent Searches */}
            <Grid item xs={12} md={4}>
              {showRecentSearches && recentSearches.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1, flexShrink: 0 }}>
                    Recent:
                  </Typography>
                  {recentSearches.slice(0, 3).map((search, index) => (
                    <Chip
                      key={index}
                      label={search.name}
                      size="small"
                      variant="outlined"
                      onClick={() => onRecentSearchClick && onRecentSearchClick(search)}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                  ))}
                  {recentSearches.length > 3 && (
                    <Button
                      size="small"
                      onClick={onClearRecentSearches}
                      sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                    >
                      Clear
                    </Button>
                  )}
                </Box>
              )}
            </Grid>

            {/* View Mode and Filters */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
                {/* View Mode Toggle */}
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="cards">
                    <ViewModuleIcon />
                  </ToggleButton>
                  <ToggleButton value="table">
                    <ViewListIcon />
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* Filters */}
                <Badge badgeContent={getActiveFiltersCount()} color="primary">
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={handleFilterClick}
                    sx={{ borderRadius: '8px', textTransform: 'none' }}
                  >
                    Filters
                  </Button>
                </Badge>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </SearchCard>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : filteredPatients.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No patients found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm ? 'Try adjusting your search criteria' : 'Get started by adding your first patient'}
            </Typography>
            {!searchTerm && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreatePatient}
                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
              >
                Add First Patient
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Results Summary */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {paginatedPatients.length} of {filteredPatients.length} patients
            </Typography>
            {getActiveFiltersCount() > 0 && (
              <Button
                size="small"
                onClick={clearFilters}
                sx={{ textTransform: 'none' }}
              >
                Clear Filters
              </Button>
            )}
          </Box>

          {/* Cards View */}
          {viewMode === 'cards' && (
            <Grid container spacing={2}>
              {paginatedPatients.map(renderPatientCard)}
            </Grid>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <Card sx={{ borderRadius: '12px' }}>
              <StyledDataGrid
                rows={paginatedPatients}
                columns={columns}
                pageSize={pageSize}
                hideFooter
                autoHeight
                disableSelectionOnClick
                disableColumnMenu
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                columnVisibilityModel={{
                  name: true,
                  mrn: true,
                  dateOfBirth: true,
                  age: true,
                  contact: true,
                  bloodType: true,
                  lastEncounter: true,
                  actions: true,
                  isPatientPortalEnabled: true,
                }}
                onRowClick={(params) => {
                  if (params && params.row && onPatientClick) {
                    onPatientClick(params.row);
                  }
                }}
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'grey.100',
                    color: 'text.primary',
                    fontWeight: 600,
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: '200px',
                  },
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              />
            </Card>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: { minWidth: 200, borderRadius: '12px' }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600 }}>
          Blood Type
        </Typography>
        {['all', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((bloodType) => (
          <MenuItem
            key={bloodType}
            selected={selectedFilters.bloodType === bloodType}
            onClick={() => {
              handleFilterChange('bloodType', bloodType);
              handleFilterClose();
            }}
          >
            {bloodType === 'all' ? 'All' : bloodType}
          </MenuItem>
        ))}
        
        <Divider sx={{ my: 1 }} />
        
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600 }}>
          Ongoing Encounter
        </Typography>
        {[
          { value: 'all', label: 'All' },
          { value: 'yes', label: 'Has Encounter' },
          { value: 'no', label: 'No Encounter' }
        ].map((option) => (
          <MenuItem
            key={option.value}
            selected={selectedFilters.hasOngoingEncounter === option.value}
            onClick={() => {
              handleFilterChange('hasOngoingEncounter', option.value);
              handleFilterClose();
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {hoveredPatient.data && (
        <PatientHoverModal
          patient={hoveredPatient.data}
          position={hoveredPatient.position}
          onEditProfile={() => {
            if (onPatientClick) {
              onPatientClick(hoveredPatient.data);
            }
          }}
        />
      )}
    </Box>
  );
};

export default PatientListBase; 